import type { TranslationKey } from '../../core/i18n/translation';

/** A labelled run of times, in the order the day runs. */
export interface SlotGroup {
    readonly label: TranslationKey;
    readonly slots: readonly string[];
}

/**
 * Splits a day's times into morning, afternoon and evening, at the live form's boundaries — before
 * 13:00, before 17:00, and the rest.
 *
 * **The hour is read off the string, not off a `Date`.** These are the server's local wall-clock
 * times; `new Date("14:30")` is not a date at all, and the constructions that do parse read the
 * device's zone and would file a 14:30 slot under "evening" for a customer three hours east.
 *
 * A group with nothing in it is dropped rather than rendered empty, so a day that only runs in the
 * afternoon shows one heading instead of three.
 */
export function groupSlots(slots: readonly string[]): SlotGroup[] {
    const groups: { label: TranslationKey; until: number; slots: string[] }[] = [
        { label: 'booking.slots.morning', until: 13, slots: [] },
        { label: 'booking.slots.afternoon', until: 17, slots: [] },
        { label: 'booking.slots.evening', until: 24, slots: [] }
    ];

    for (const slot of slots) {
        const hour = Number(slot.slice(0, 2));

        (groups.find((group) => hour < group.until) ?? groups[groups.length - 1]).slots.push(slot);
    }

    return groups
        .filter((group) => group.slots.length > 0)
        .map(({ label, slots: grouped }) => ({ label, slots: grouped }));
}
